/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { isIOS } from "../../util/UserAgent";
import debug from "../../util/constants/Debug";

// grid layout constants
const gridRowCount = 48;
const gridRowPx = { min: 0, max: 18 };    // TODO: Change min to some reasonable value (10 seems good)
export const gridRowDefaultHeight = 15;

// grid layout value functions
export const containerHeightToGridRow = (height) => {
  // default is max height
  if (height == null) {
    return gridRowDefaultHeight;
  }

  // calculate grid row height based on window height
  const rowHeight = Math.floor(height / gridRowCount);

  // return height within bounds
  if (rowHeight > gridRowPx.max) {
    return gridRowPx.max;
  }
  if (rowHeight < gridRowPx.min) {
    return gridRowPx.min;
  }

  return rowHeight;
};

export const containerHeightToGridHeight = (height) => {
  if (height == null) {
    return null;
  }

  const rowHeight = containerHeightToGridRow(height);
  return rowHeight * gridRowCount;
}

export const gridLayoutConstants = (height = null) => ({
  rowHeight: containerHeightToGridRow(height),
  cols: 32,
  margin: [0, 0],
  compactType: null
});

// grid item dimension values
const defaultGridItemDimensions = {
  height: 6,
  minHeight: 2,
  maxHeight: Infinity,
  width: 6,
  minWidth: 4,
  maxWidth: Infinity
};

export const gridItemDimensions = {
  label: {
    ...defaultGridItemDimensions,
    height: 2,
    width: 8,
    minWidth: 5
  },
  image: {
    ...defaultGridItemDimensions,
    height: 6,
    width: 6,
  },
  loginFields: {
    ...defaultGridItemDimensions,
    height: 6,
    minHeight: 4,
    maxHeight: 14,
    width: 10,
    minWidth: 6
  },
  field: {
    ...defaultGridItemDimensions,
    height: 2,
    maxHeight: 6,
    width: 10,
    minWidth: 6
  },
  button: {
    ...defaultGridItemDimensions,
    height: 4,
    width: 8,
  },
  shape: {
    ...defaultGridItemDimensions,
    height: 3,
    width: 5
  },
  socialButton: {
    ...defaultGridItemDimensions,
    height: 3,
    minHeight: 3,
    width: 20,
    minWidth: 4
  }
};

// grid item form standard values
const labelColor = "#4888D0";
const autoFillLabelColor = "#6070B0";
const imageColor = "#805858";
const interactableColor = "#508898";
const shapeColor = "#888090";
const socialMediaColor = "#906080";

const standardLabelForm = [
  { divider: true },
  { type: "size5", id: "size", name: "Text Size" },
  { type: "textAlign", id: "align", name: "Text Alignment" },
  { type: "textStyle", id: "style", name: "Text Style" },
  { type: "font", id: "font", name: "Font" },
  { type: "color", id: "color", name: "Text Color" },
];

const standardPrefixSuffixLabelForm = [
  { type: "text", id: "prefix", name: "Before" },
  { type: "text", id: "suffix", name: "After" },
  ...standardLabelForm,
];

const standardImageForm = [
  { type: "slider", id: "size", name: "Image Size" },
  { divider: true },
  { type: "vhalign", id: "boxAlign", name: "Bounding Box Alignment" },
  { type: "hsize", id: "boxMaxWidth", name: "Maximum Width"},
];

const standardFieldForm = [
  { type: "text", id: "placeholder", name: "Placeholder Text" },
  { type: "size5", id: "placeholderSize", name: "Placeholder Size"},
  { divider: true },
  { type: "halign", id: "boxAlign", name: "Box Alignment"},
  { type: "hsize", id: "boxMaxWidth", name: "Maximum Width"},
];

const standardButtonForm = [
  { type: "buttonType", id: "type", name: "Type" },
  { type: "color", id: "buttonColor", name: "Button Color" },
  { divider: true },
  { type: "size5", id: "textSize", name: "Text Size" },
  { type: "textStyle", id: "textStyle", name: "Text Style" },
  { type: "textAlign", id: "textAlign", name: "Text Alignment" },
  { type: "color", id: "textColor", name: "Text Color" },
  { divider: true },
  { type: "halign", id: "boxAlign", name: "Box Alignment" },
  { type: "hsize", id: "boxMaxWidth", name: "Maximum Width"},
];

const standardShapeForm = [
  { type: "colorAlpha", id: "color", name: "Color" },
  { type: "colorAlpha", id: "borderColor", name: "Border Color" },
  { divider: true },
  { type: "halign", id: "boxAlign", name: "Box Alignment" },
  { type: "hsize", id: "boxMaxWidth", name: "Maximum Width"},
]

const standardSocialButtonForm = [
  { type: "buttonShape", id: "shape", name: "Shape" },
  { type: "textAlign", id: "textAlign", name: "Text Alignment" },
  { divider: true },
  { type: "halign", id: "boxAlign", name: "Box Alignment" },
  { type: "hsize", id: "boxMaxWidth", name: "Maximum Width"},
];

// grid item default standard configurations
const standardConfig = {
  visible: true,
};

const standardLabelConfig = {
  ...standardConfig,
  size: "sm",
  style: {
    bold: false,
    underline: false,
    italic: false
  },
  align: "left",
  color: "#000000",
  boxAlign: "top left",
  boxMaxWidth: "",
};

const standardPrefixSuffixLabelConfig = {
  ...standardLabelConfig,
  prefix: "",
  suffix: "",
};

const standardImageConfig = {
  ...standardConfig,
  boxAlign: "middle center",
  size: {
    auto: true,
    type: "height",
    height: "200",
    width: "200"
  },
};

const standardFieldConfig = {
  ...standardConfig,
  placeholderSize: "sm",
  boxAlign: "center",
  boxMaxWidth: "",
};

const standardButtonConfig = {
  ...standardConfig,
  type: "button",
  textSize: "md",
  textStyle: {
    bold: false,
    underline: false,
    italic: false
  },
  textAlign: "center",
  textColor: "#FFFFFF",
  buttonColor: "#303030",
  boxAlign: "center",
  boxMaxWidth: "",
};

const standardShapeConfig = {
  ...standardConfig,
  color: "#80808080",
  borderColor: "#00000080",
  boxAlign: "center",
  boxMaxWidth: "",
};

const standardSocialButtonConfig = {
  ...standardConfig,
  shape: "rectangle",
  textAlign: "left",
  boxAlign: "center",
  boxMaxWidth: "",
};

// grid item attributes
export const gridItemAttributes = {
  customLabel: {
    name: "Custom Label",
    dragName: "Custom",
    dimension: "label",
    icon: "bi bi-pencil-square",
    color: labelColor,
    form: [
      { divider: true },
      { type: "text", id: "text", name: "Text" },
      { type: "size5", id: "size", name: "Text Size" },
      { type: "bodyText", id: "bodyText", name: "Body Text" },
      { type: "size5", id: "bodySize", name: "Body Text Size"},
      { type: "textAlign", id: "align", name: "Text Alignment" },
      { type: "textStyle", id: "style", name: "Text Style" },
      { type: "font", id: "font", name: "Font" },
      { type: "color", id: "color", name: "Text Color" },
    ],
    defaultConfig: {
      ...standardLabelConfig,
      text: "",
      bodyText: "",
    },
  },
  businessName: {
    name: "Business Label",
    dragName: "Business",
    dimension: "label",
    icon: "bi bi-briefcase-fill",
    color: autoFillLabelColor,
    form: standardPrefixSuffixLabelForm,
    defaultConfig: standardPrefixSuffixLabelConfig,
  },
  locationName: {
    name: "Location Label",
    dragName: "Location",
    dimension: "label",
    icon: "bi bi-building-fill",
    color: autoFillLabelColor,
    form: standardPrefixSuffixLabelForm,
    defaultConfig: standardPrefixSuffixLabelConfig,
  },
  routerName: {
    name: "Router Label",
    dragName: "Router",
    dimension: "label",
    icon: "bi bi-router",
    color: autoFillLabelColor,
    form: standardPrefixSuffixLabelForm,
    defaultConfig: standardPrefixSuffixLabelConfig,
  },
  emailLabel: {
    name: "Email Label",
    dragName: "Email",
    dimension: "label",
    icon: "bi bi-envelope-at-fill",
    color: autoFillLabelColor,
    form: standardPrefixSuffixLabelForm,
    defaultConfig: standardPrefixSuffixLabelConfig,
  },
  websiteLabel: {
    name: "Website Label",
    dragName: "Website",
    dimension: "label",
    icon: "bi bi-globe2",
    color: autoFillLabelColor,
    form: standardPrefixSuffixLabelForm,
    defaultConfig: standardPrefixSuffixLabelConfig,
  },
  addressLabel: {
    name: "Address Label",
    dragName: "Address",
    dimension: "label",
    icon: "bi bi-geo-alt-fill",
    color: autoFillLabelColor,
    form: standardPrefixSuffixLabelForm,
    defaultConfig: standardPrefixSuffixLabelConfig,
  },
  logo: {
    name: "Logo",
    dimension: "image",
    icon: "bi bi-file-image",
    color: imageColor,
    form: standardImageForm,
    defaultConfig: standardImageConfig
  },
  staticImage: {
    name: "Static Image",
    dimension: "image",
    icon: "bi bi-image",
    color: imageColor,
    form: [
      { type: "image", id: "url", name: "Image" },
      { divider: true },
      ...standardImageForm,
    ],
    defaultConfig: {
      url: null,
      ...standardImageConfig,
    },
  },
  loginFields: {
    name: "Login Fields",
    dimension: "loginFields",
    icon: "bi bi-box-arrow-in-right",
    form: [
      { type: "loginMethod", id: "loginMethod", name: "Login Method" },
      { divider: true },
      { type: "hsize", id: "boxMaxWidth", name: "Maximum Width" },
    ],
    color: interactableColor,
    defaultConfig: {
      ...standardFieldConfig,
      loginMethod: "username"
    },
  },
  usernameField: {
    name: "Username Field",
    dimension: "field",
    icon: "bi bi-file-person",
    form: standardFieldForm,
    color: interactableColor,
    defaultConfig: {
      ...standardFieldConfig,
      placeholder: "Username",
    },
  },
  passwordField: {
    name: "Password Field",
    dimension: "field",
    icon: "bi bi-file-lock2",
    color: interactableColor,
    form: standardFieldForm,
    defaultConfig: {
      ...standardFieldConfig,
      placeholder: "Password",
    },
  },
  smsField: {
    name: "SMS Field",
    dimension: "field",
    icon: "bi bi-telephone-fill",
    color: interactableColor,
    form: standardFieldForm,
    defaultConfig: {
      ...standardFieldConfig,
      placeholder: "Phone Number",
    },
  },
  smsPasswordField: {
    name: "SMS Password",
    dimension: "field",
    icon: "bi bi-file-lock2",
    color: interactableColor,
    form: standardFieldForm,
    defaultConfig: {
      ...standardFieldConfig,
      placeholder: "Password",
    },
  },
  submitButton: {
    name: "Submit Button",
    dragName: "Login Button",
    dimension: "button",
    icon: "bi bi-person-check-fill",
    color: interactableColor,
    form: [
      { type: "text", id: "text", name: "Text" },
      ...standardButtonForm
    ],
    defaultConfig: {
      ...standardButtonConfig,
      text: "Login",
    }
  },
  redirectButton: {
    name: "Redirect Button",
    dimension: "button",
    icon: "bi bi-box-arrow-up-right",
    color: interactableColor,
    form: [
      { type: "text", id: "url", name: "Redirect URL" },
      { type: "text", id: "text", name: "Text" },
      ...standardButtonForm,
    ],
    defaultConfig: {
      ...standardButtonConfig,
      text: "Redirect"
    }
  },
  signupButton: {
    name: "Signup Button",
    dimension: "button",
    icon: "bi bi-person-plus-fill",
    color: interactableColor,
    form: standardButtonForm,
    defaultConfig: standardButtonConfig
  },
  localizationBar: {
    name: "Language Bar",
    dimension: "button",
    icon: "bi bi-translate",
    color: interactableColor,
    form: [
      { type: "language", id: "language", name: "Language" },
      { divider: true },
      { type: "halign", id: "boxAlign", name: "Box Alignment" },
      { type: "hsize", id: "boxMaxWidth", name: "Maximum Width" },
    ],
    defaultConfig: {
      localization: ""
    }
  },
  rectangle: {
    name: "Rectangle",
    dimension: "shape",
    icon: "bi bi-square-fill",
    color: shapeColor,
    form: standardShapeForm,
    defaultConfig: standardShapeConfig
  },
  googleLogin: {
    name: "Google Login",
    dragName: "Google",
    dimension: "socialButton",
    icon: "bi bi-google",
    color: socialMediaColor,
    form: standardSocialButtonForm,
    defaultConfig: standardSocialButtonConfig
  },
  appleLogin: {
    name: "Apple Login",
    dragName: "Apple",
    dimension: "socialButton",
    icon: "bi bi-apple",
    color: socialMediaColor,
    form: standardSocialButtonForm,
    defaultConfig: standardSocialButtonConfig
  },
  facebookLogin: {
    name: "Facebook Login",
    dragName: "Facebook",
    dimension: "socialButton",
    icon: "bi bi-facebook",
    color: socialMediaColor,
    form: standardSocialButtonForm,
    defaultConfig: standardSocialButtonConfig
  },
  linkedinLogin: {
    name: "Linkedin Login",
    dragName: "Linkedin",
    dimension: "socialButton",
    icon: "bi bi-linkedin",
    color: socialMediaColor,
    form: standardSocialButtonForm,
    defaultConfig: standardSocialButtonConfig
  },
  twitterLogin: {
    name: "Twitter Login",
    dragName: "Twitter",
    dimension: "socialButton",
    icon: "bi bi-twitter",
    color: socialMediaColor,
    form: standardSocialButtonForm,
    defaultConfig: standardSocialButtonConfig
  },
};

// draggable grid item values
export const foregroundDraggableItems = [
  {
    name: "Labels",
    options: [
      "customLabel",
    ]
  },
  {
    name: "Auto-Fill Labels",
    options: [
      "businessName",
      "locationName",
      "routerName",
      "emailLabel",
      "websiteLabel",
      // "addressLabel",
    ]
  },
  {
    name: "Images",
    options: [
      "logo",
      "staticImage",
    ]
  },
  {
    name: "Interactable",
    options: [
      "loginFields",
      "usernameField",
      "passwordField",
      "smsField",
      "smsPasswordField",
      "submitButton",
      "redirectButton",
      "signupButton",
      "localizationBar",
    ]
  },
  {
    name: "Social Media",
    options: [
      "googleLogin",
      "appleLogin",
      "facebookLogin",
      "linkedinLogin",
      "twitterLogin",
    ]
  }
];

export const framingDraggableItems = [
  {
    name: "Framing",
    options: [
      "rectangle",
    ]
  },
  {
    name: "Images",
    options: [
      "logo",
      "staticImage",
    ]
  }
];

// default layout values
export const defaultBackgroundColor1 = "#EFEFF4";
export const defaultBackgroundColor2 = "#AFAFB4";
export const defaultBackgroundGradientDirection = 90;

export const defaultCaptivePortalConfig = {
  foreground: [
    {
      row: 10,
      col: 3,
      height: 3,
      width: 12,
      type: "usernameField",
      config: {
        ...standardFieldConfig,
        placeholder: "Username"
      }
    },
    {
      row: 10,
      col: 7,
      height: 3,
      width: 12,
      type: "passwordField",
      config: {
        ...standardFieldConfig,
        placeholder: "Password"
      }
    },
    {
      row: 12,
      col: 12,
      height: 3,
      width: 8,
      type: "submitButton",
      config: {
        ...standardButtonConfig,
        text: "Submit"
      }
    },
  ],
  framing: [
    {
      row: 8,
      col: 2,
      height: 14,
      width: 16,
      type: "rectangle",
      config: {
        color: "#80808080",
        borderColor: "#80808080",
      }
    }
  ],
  background: {
    type: "color",
    color: defaultBackgroundColor1,
    gradientColor1: defaultBackgroundColor1,
    gradientColor2: defaultBackgroundColor2,
    gradientDirection: defaultBackgroundGradientDirection,
    image: undefined
  }
};

// helper functions
export const getGridInfo = (type) => {
  try {
    const attributes = gridItemAttributes[type];
    const dimension = gridItemDimensions[attributes.dimension];
    return { type, attributes, dimension};
  } catch (error) {
    debug.warn(`Error getting info for type: ${type}: ${error}`);
    return null;
  }
};

export const backgroundConfigToStyle = (config, dimension = null, absolute = true) => {
  // common attributes
  const dimensionAttributes = {
    width: dimension?.width ?? "100%",
    height: dimension?.height ?? "100%",
    position: absolute ? "absolute" : "static"
  };

  // device-specific attributes
  const deviceAttributes = isIOS() ? {
    paddingTop: "env(safe-area-inset-top)",
    paddingBottom: "env(safe-area-inset-bottom)",
    paddingLeft: "env(safe-area-inset-left)",
    paddingRight: "env(safe-area-inset-right)",
  } : {};

  // combined attributes
  const commonAttributes = {
    ...dimensionAttributes,
    ...deviceAttributes
  };

  // handle null config
  if (config == null) {
    return commonAttributes
  }

  // return config css style object based on style
  switch (config.type) {
    case "color":
      return {
        ...commonAttributes,
        backgroundColor: config.color ?? defaultBackgroundColor1,
      };
    case "gradient":
      return {
        ...commonAttributes,
        backgroundImage: `linear-gradient(
          ${config.gradientDirection ?? defaultBackgroundGradientDirection}deg,
          ${config.gradientColor1 ?? defaultBackgroundColor1},
          ${config.gradientColor2 ?? defaultBackgroundColor2})`,
      };
    case "image":
      return {
        ...commonAttributes,
        backgroundImage: `url(${config.image})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      };
    // handle invalid config
    default: return commonAttributes;
  }
};

// encode/decode functions
const layerEncodingToTemplate = (layerEncoding) => {
  return layerEncoding.map((item, index) => {
    const dimension = getGridInfo(item.type)?.dimension;
    return {
      i: index.toString(),
      x: item.row,
      y: item.col,
      h: item.height,
      w: item.width,
      minW: dimension.minWidth,
      maxW: dimension.maxWidth,
      minH: dimension.minHeight,
      maxH: dimension.maxHeight,
    }
  });
};

const layerEncodingToContent = (layerEncoding) => {
  return layerEncoding.map((item, index) => ({
    i: index.toString(),
    type: item.type,
    config: item.config
  }));
};

const decodeLayer = (layer) => {
  return {
    template: layerEncodingToTemplate(layer),
    content: layerEncodingToContent(layer)
  };
};

export const decodeLayout = (config) => {
  if (config == null) {
    return null
  }
  try {
    const decodedConfig = {
      foreground: decodeLayer(config.foreground),
      framing: decodeLayer(config.framing),
      background: config.background
    };
    return decodedConfig;
  } catch (error) {
    debug.warn("Error decoding layout: " + error);
    return null;
  }
};

const encodeLayer = (layout) => {
  return layout.template.map((item) => {
    const content = layout.content.find((layoutContent) => layoutContent.i === item.i);
    return {
      row: item.x,
      col: item.y,
      height: item.h,
      width: item.w,
      type: content.type,
      config: content.config
    }
  });
};

export const encodeLayout = (foreground, framing, background) => {
  return {
    foreground: encodeLayer(foreground),
    framing: encodeLayer(framing),
    background: background
  };
};

// other util
export const hierarchyListToFields = (hierarchyList) => {
  // handle null
  if (!hierarchyList) {
    return null;
  }

  // constants
  const keys = [ "businessName", "locationName", "routerName", "email", "phoneNumber", "website", "logoUrl", "splashScreenUrl" ];
  const fields = {};

  // build dictionary of fields
  keys.forEach((key) => {
    const hierarchyItem = hierarchyList.find((hierarchyItem) => hierarchyItem[key]);
    if (hierarchyItem) {
      fields[key] = hierarchyItem[key];
    }
  });

  debug.log("Fields");
  debug.log(fields);

  return fields;
}

export const clientHasSmsLogin = (client) => {
  if (!client || !client.locations || client.locations.length === 0) { 
    return false;
  }

  const businessClientCP = client.captivePortals;
  for (const location of client.locations) {
    if (location.routers && location.routers.length > 0) {
      for (const router of location.routers) {
        if (router.captivePortals && !hasSmsLogin(router.captivePortals)) { 
          return false;
        } else if (location.captivePortals && !hasSmsLogin(location.captivePortals)) {
          return false;
        } else if (client.captivePortals && !hasSmsLogin(businessClientCP)) {
          return false;
        }
      }
    } else {
      return false;
    }
  }

  return true;
}

export const hasSmsLogin = (captivePortals) => {
  if (!captivePortals) {
    return null;
  }

  // Parse to layouts
  return captivePortals.some((item) => {
    if (!item.default && !item.schedule) {
      return false;
    }

    var foregroundItems = item.layout?.config?.foreground ?? [];
    if (foregroundItems.some((item) => item.config?.loginMethod?.methods?.some((method) => method === "sms" ))) {
      return true;
    }

    return foregroundItems.some((item) => {
      return  item.type === "smsField"
    }) && foregroundItems.some((item) => {
      return item.type === "smsPasswordField";
    })
  });
}
